import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend
} from 'recharts';

const ClusterTimeseriesChart = ({ timeseries = [], loading = false, period = '24h' }) => {
  const { t, i18n } = useTranslation();
  const [viewMode, setViewMode] = useState('efficiency'); // 'efficiency' | 'allocation' | 'waste'

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat(i18n.language || 'en', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const formatTimestampTick = (isoStr) => {
    if (!isoStr) return '';
    try {
      const d = new Date(isoStr);
      if (isNaN(d.getTime())) return isoStr;
      if (period === '7d') {
        return new Intl.DateTimeFormat(i18n.language || 'en', {
          month: 'numeric',
          day: 'numeric',
          hour: '2-digit'
        }).format(d);
      }
      return new Intl.DateTimeFormat(i18n.language || 'en', {
        hour: '2-digit',
        minute: '2-digit'
      }).format(d);
    } catch {
      return isoStr;
    }
  };

  const formatTooltipTime = (isoStr) => {
    if (!isoStr) return '';
    try {
      const d = new Date(isoStr);
      if (isNaN(d.getTime())) return isoStr;
      return new Intl.DateTimeFormat(i18n.language || 'en', {
        dateStyle: 'short',
        timeStyle: 'short'
      }).format(d);
    } catch {
      return isoStr;
    }
  };

  const chartData = timeseries.map((item, index) => {
    const rawCpu = item.cpu_efficiency_percent ?? item.cpu_efficiency ?? 0;
    const rawMem = item.memory_efficiency_percent ?? item.memory_efficiency ?? 0;
    const timeLabel = item.window_start || item.window_end || `T${index}`;

    return {
      rawTime: timeLabel,
      formattedTime: formatTimestampTick(timeLabel),
      cpuEfficiency: parseFloat(Number(rawCpu).toFixed(1)),
      memEfficiency: parseFloat(Number(rawMem).toFixed(1)),
      totalCpu: parseFloat(Number(item.total_cpu_millicores || 0).toFixed(0)),
      usedCpu: parseFloat(Number(item.used_cpu_millicores || 0).toFixed(0)),
      totalMem: parseFloat(Number(item.total_memory_mb || 0).toFixed(0)),
      usedMem: parseFloat(Number(item.used_memory_mb || 0).toFixed(0)),
      costWaste: parseFloat(Number(item.estimated_cost_waste_usd || 0).toFixed(2))
    };
  });

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload || {};
      return (
        <div style={{
          backgroundColor: '#0F172A',
          border: '1px solid rgba(99, 102, 241, 0.4)',
          padding: '12px 16px',
          borderRadius: '10px',
          boxShadow: '0 8px 30px rgba(0,0,0,0.5)',
          textAlign: 'left',
          minWidth: '180px'
        }}>
          <p style={{ color: '#E2E8F0', margin: '0 0 8px 0', fontWeight: 'bold', fontSize: '0.85rem' }}>
            📅 {formatTooltipTime(data.rawTime)}
          </p>

          {viewMode === 'efficiency' && (
            <>
              <p style={{ color: '#94A3B8', margin: '0 0 4px 0', fontSize: '0.8rem' }}>
                ⚡ {t('finops.telemetry.cpu_efficiency')}: <span style={{ color: '#38BDF8', fontWeight: '700' }}>{data.cpuEfficiency}%</span>
              </p>
              <p style={{ color: '#94A3B8', margin: '0', fontSize: '0.8rem' }}>
                🧠 {t('finops.telemetry.memory_efficiency')}: <span style={{ color: '#A78BFA', fontWeight: '700' }}>{data.memEfficiency}%</span>
              </p>
            </>
          )}

          {viewMode === 'allocation' && (
            <>
              <div style={{ borderBottom: '1px solid rgba(255,255,255,0.1)', paddingBottom: '4px', marginBottom: '4px' }}>
                <span style={{ fontSize: '0.75rem', color: '#94A3B8', fontWeight: '700' }}>{t('finops.telemetry.cpu_millicores')}:</span>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', color: '#E2E8F0' }}>
                  <span>{t('finops.telemetry.used')}: <strong style={{ color: '#34D399' }}>{data.usedCpu}</strong></span>
                  <span>/ {t('finops.telemetry.total_allocated')}: <strong>{data.totalCpu}</strong></span>
                </div>
              </div>
              <div>
                <span style={{ fontSize: '0.75rem', color: '#94A3B8', fontWeight: '700' }}>{t('finops.telemetry.memory_mb')}:</span>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', color: '#E2E8F0' }}>
                  <span>{t('finops.telemetry.used')}: <strong style={{ color: '#60A5FA' }}>{data.usedMem}</strong></span>
                  <span>/ {t('finops.telemetry.total_allocated')}: <strong>{data.totalMem}</strong></span>
                </div>
              </div>
            </>
          )}

          {viewMode === 'waste' && (
            <p style={{ color: '#94A3B8', margin: '0', fontSize: '0.8rem' }}>
              💸 {t('finops.telemetry.estimated_waste')}: <span style={{ color: '#F87171', fontWeight: '700' }}>{formatCurrency(data.costWaste)}</span>
            </p>
          )}
        </div>
      );
    }
    return null;
  };

  const cardStyle = {
    background: 'rgba(15, 23, 42, 0.65)',
    border: '1px solid rgba(79, 70, 229, 0.28)',
    borderRadius: '16px',
    padding: '24px',
    backdropFilter: 'blur(14px)',
    WebkitBackdropFilter: 'blur(14px)',
    boxShadow: '0 8px 32px rgba(0,0,0,0.35)',
    marginBottom: '30px',
    minWidth: 0
  };

  const buttonStyle = (active) => ({
    padding: '6px 14px',
    borderRadius: '8px',
    fontSize: '0.75rem',
    fontWeight: '700',
    border: 'none',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    background: active ? 'rgba(99, 102, 241, 0.9)' : 'rgba(255,255,255,0.06)',
    color: active ? '#FFFFFF' : '#94A3B8'
  });

  return (
    <div style={cardStyle} className="min-w-0">
      {/* Header & Mode Selectors */}
      <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: '15px', marginBottom: '20px' }}>
        <div>
          <h3 style={{ margin: '0 0 4px 0', fontSize: '1.2rem', fontWeight: '700', color: '#E2E8F0' }}>
            📈 {t('finops.telemetry.timeseries_title')}
          </h3>
          <span style={{ fontSize: '0.8rem', color: '#94A3B8' }}>
            {period === '7d' ? t('finops.telemetry.period_7d') : t('finops.telemetry.period_24h')} • {chartData.length} {t('finops.telemetry.data_points')}
          </span>
        </div>

        {/* View Mode Toggle Switch */}
        <div style={{ display: 'flex', gap: '8px', background: 'rgba(15, 23, 42, 0.8)', padding: '4px', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.08)' }}>
          <button
            onClick={() => setViewMode('efficiency')}
            style={buttonStyle(viewMode === 'efficiency')}
          >
            {t('finops.telemetry.timeseries_efficiency_view')}
          </button>
          <button
            onClick={() => setViewMode('allocation')}
            style={buttonStyle(viewMode === 'allocation')}
          >
            {t('finops.telemetry.timeseries_allocation_view')}
          </button>
          <button
            onClick={() => setViewMode('waste')}
            style={buttonStyle(viewMode === 'waste')}
          >
            {t('finops.telemetry.timeseries_waste_view')}
          </button>
        </div>
      </div>

      {/* Chart Canvas */}
      <div style={{ height: '340px', width: '100%', minWidth: 0, minHeight: '250px', position: 'relative' }} className="min-w-0">
        {loading ? (
          <div style={{ height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', color: '#94A3B8' }}>
            <span className="spinner" style={{
              display: 'inline-block',
              width: '32px',
              height: '32px',
              border: '3px solid rgba(99, 102, 241, 0.2)',
              borderTopColor: '#6366F1',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              marginRight: '12px'
            }}></span>
            {t('finops.telemetry.telemetry_loading')}
          </div>
        ) : chartData.length === 0 ? (
          <div style={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', color: '#94A3B8', textAlign: 'center' }}>
            <span style={{ fontSize: '2.5rem', marginBottom: '8px' }}>📉</span>
            <p style={{ margin: 0, fontSize: '0.95rem' }}>{t('finops.telemetry.no_telemetry_data')}</p>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={250}>
            {viewMode === 'waste' ? (
              <BarChart data={chartData} margin={{ top: 10, right: 10, left: -10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" vertical={false} />
                <XAxis dataKey="formattedTime" stroke="#64748B" fontSize={11} tickLine={false} />
                <YAxis stroke="#64748B" fontSize={11} tickLine={false} tickFormatter={(v) => `$${v}`} />
                <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(255,255,255,0.03)' }} />
                <Legend
                  verticalAlign="bottom"
                  height={30}
                  wrapperStyle={{ fontSize: '11px', paddingTop: '8px' }}
                  formatter={() => <span style={{ color: '#E2E8F0' }}>{t('finops.telemetry.estimated_waste')} ($ USD)</span>}
                />
                <Bar dataKey="costWaste" fill="#EF4444" radius={[4, 4, 0, 0]} isAnimationActive={true} />
              </BarChart>
            ) : viewMode === 'allocation' ? (
              <AreaChart data={chartData} margin={{ top: 10, right: 10, left: -10, bottom: 5 }}>
                <defs>
                  <linearGradient id="totalCpuGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#38BDF8" stopOpacity={0.4} />
                    <stop offset="95%" stopColor="#38BDF8" stopOpacity={0.0} />
                  </linearGradient>
                  <linearGradient id="usedCpuGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10B981" stopOpacity={0.6} />
                    <stop offset="95%" stopColor="#10B981" stopOpacity={0.0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" vertical={false} />
                <XAxis dataKey="formattedTime" stroke="#64748B" fontSize={11} tickLine={false} />
                <YAxis stroke="#64748B" fontSize={11} tickLine={false} />
                <Tooltip content={<CustomTooltip />} />
                <Legend
                  verticalAlign="bottom"
                  height={30}
                  wrapperStyle={{ fontSize: '11px', paddingTop: '8px' }}
                  formatter={(val) => {
                    if (val === 'totalCpu') return <span style={{ color: '#38BDF8' }}>{t('finops.telemetry.total_cpu')}</span>;
                    if (val === 'usedCpu') return <span style={{ color: '#10B981' }}>{t('finops.telemetry.used_cpu')}</span>;
                    return val;
                  }}
                />
                <Area type="monotone" dataKey="totalCpu" stroke="#38BDF8" strokeWidth={2} fillOpacity={1} fill="url(#totalCpuGrad)" isAnimationActive={true} />
                <Area type="monotone" dataKey="usedCpu" stroke="#10B981" strokeWidth={2} fillOpacity={1} fill="url(#usedCpuGrad)" isAnimationActive={true} />
              </AreaChart>
            ) : (
              <AreaChart data={chartData} margin={{ top: 10, right: 10, left: -10, bottom: 5 }}>
                <defs>
                  <linearGradient id="cpuGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#38BDF8" stopOpacity={0.4} />
                    <stop offset="95%" stopColor="#38BDF8" stopOpacity={0.0} />
                  </linearGradient>
                  <linearGradient id="memGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#A78BFA" stopOpacity={0.4} />
                    <stop offset="95%" stopColor="#A78BFA" stopOpacity={0.0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" vertical={false} />
                <XAxis dataKey="formattedTime" stroke="#64748B" fontSize={11} tickLine={false} />
                <YAxis stroke="#64748B" fontSize={11} tickLine={false} domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
                <Tooltip content={<CustomTooltip />} />
                <Legend
                  verticalAlign="bottom"
                  height={30}
                  wrapperStyle={{ fontSize: '11px', paddingTop: '8px' }}
                  formatter={(val) => {
                    if (val === 'cpuEfficiency') return <span style={{ color: '#38BDF8' }}>{t('finops.telemetry.cpu_efficiency')} (%)</span>;
                    if (val === 'memEfficiency') return <span style={{ color: '#A78BFA' }}>{t('finops.telemetry.memory_efficiency')} (%)</span>;
                    return val;
                  }}
                />
                <Area type="monotone" dataKey="cpuEfficiency" stroke="#38BDF8" strokeWidth={2} fillOpacity={1} fill="url(#cpuGrad)" isAnimationActive={true} />
                <Area type="monotone" dataKey="memEfficiency" stroke="#A78BFA" strokeWidth={2} fillOpacity={1} fill="url(#memGrad)" isAnimationActive={true} />
              </AreaChart>
            )}
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
};

export default ClusterTimeseriesChart;
